import { newModelPricingForKind, TOKEN_MULTIPLIER_MIN, TOKEN_MULTIPLIER_MAX, type AiPricing } from "@/lib/ai/pricing";
import { normalizeInputLimits, type AiInputLimits } from "./input-limits";

export const AI_MODEL_KINDS = [
  "chat",
  "embedding",
  "rerank",
  "image_gen",
  "video_gen",
  "tts",
  "stt",
] as const;

export const AI_LITE_MODEL_SLOT = "lite";

export const AI_TRANSLATE_MODEL_SLOT = "translate";

export const AI_SITE_CHAT_SLOTS = [AI_LITE_MODEL_SLOT, AI_TRANSLATE_MODEL_SLOT] as const;

export const AI_MODEL_BILLING_MODES = ["token", "spec"] as const;

export const AI_MODEL_BILLING_MODE_LABELS: Record<string, string> = {
  token: "按量计费",
  spec: "按档计费",
};

export const AI_MODEL_BILLING_MODE_DESCRIPTIONS: Record<string, string> = {
  token: "按输入 / 输出 token 量结算，单价以每 100 万 token 计",
  spec: "按规格档位扣分，与 token 用量无关；没有规格类时就是一个固定价",
};

const AI_MODEL_KINDS_WITHOUT_OUTPUT_PRICE = ["embedding", "rerank"] as const;

export const AI_MODEL_INPUT_MODALITIES = [
  "text",
  "image",
  "audio",
  "video",
  "file",
] as const;

export const AI_MODEL_CAPABILITIES = [
  "tools",
  "reasoning",
  "json",
  "caching",
  "websearch",
] as const;

export const AI_MODEL_KIND_LABELS: Record<string, string> = {
  chat: "对话模型",
  embedding: "向量检索",
  rerank: "检索重排",
  image_gen: "图像模型",
  video_gen: "视频模型",
  tts: "语音合成",
  stt: "语音识别",
};

export const AI_MODEL_KIND_DESCRIPTIONS: Record<string, string> = {
  chat: "多轮对话；可配置输入模态与功能能力",
  embedding: "将资料或问题转换为向量，用于知识库入库与查询检索",
  rerank: "对检索到的资料重新评分、排序",
  image_gen: "文字生图，可配置参考图及文档附件输入",
  video_gen: "文生视频、图生视频；异步出片，按次或按秒计价",
  tts: "文本转语音",
  stt: "语音转文本",
};

export const AI_MODEL_INPUT_MODALITY_LABELS: Record<string, string> = {
  text: "文本",
  image: "图像",
  audio: "音频",
  video: "视频",
  file: "其他附件",
};

export const AI_MODEL_CAPABILITY_LABELS: Record<string, string> = {
  tools: "工具调用",
  reasoning: "思考推理",
  json: "结构化输出",
  caching: "提示词缓存",
  websearch: "联网搜索",
};

export const AI_MODEL_KINDS_WITH_MODALITIES = [
  "chat",
  "image_gen",
  "video_gen",
  "tts",
  "stt",
] as const;

export const AI_MODEL_KINDS_WITH_CAPABILITIES = ["chat"] as const;

export const AI_MODEL_KINDS_WITH_CONTEXT = ["chat"] as const;

function toOptions(values: readonly string[], labels: Record<string, string>) {
  return values.map((value) => ({ value, label: labels[value] ?? value }));
}

export const AI_MODEL_KIND_OPTIONS = toOptions(
  AI_MODEL_KINDS,
  AI_MODEL_KIND_LABELS,
);

export const AI_MODEL_INPUT_MODALITY_OPTIONS = toOptions(
  AI_MODEL_INPUT_MODALITIES.filter(value => value !== "text"),
  AI_MODEL_INPUT_MODALITY_LABELS,
);

export const AI_IMAGE_INPUT_MODALITIES = ["text", "image", "file"] as const;
export const AI_IMAGE_INPUT_MODALITY_OPTIONS = toOptions(
  AI_IMAGE_INPUT_MODALITIES.filter(value => value !== "text"),
  AI_MODEL_INPUT_MODALITY_LABELS,
);

export function modelInputModalityOptions(kind: string) {
  if (kind === "image_gen") return AI_IMAGE_INPUT_MODALITY_OPTIONS;
  if (kind === "video_gen") return AI_MODEL_INPUT_MODALITY_OPTIONS.filter(option => ["image", "video"].includes(option.value));
  if (kind === "stt") return [{ value: "audio", label: AI_MODEL_INPUT_MODALITY_LABELS.audio, disabled: true }];
  if (kind === "tts") return [];
  return AI_MODEL_INPUT_MODALITY_OPTIONS;
}

export function normalizeImageInputModalities(input: readonly string[] = []): string[] {
  const optional = input.filter(item =>
    item !== "text" && (AI_IMAGE_INPUT_MODALITIES as readonly string[]).includes(item),
  );
  return ["text", ...new Set(optional)];
}

export function normalizeModelInputModalities(kind: string, input: readonly string[] = []): string[] {
  const tags = AI_MODEL_INPUT_MODALITIES.filter(value => input.includes(value));
  if (kind === "image_gen") return normalizeImageInputModalities(tags);
  if (kind === "chat") return ["text", ...tags.filter(value => value !== "text")];
  if (kind === "tts") return ["text"];
  if (kind === "stt") return ["audio"];
  if (kind === "video_gen") return tags.filter(value => ["image", "video"].includes(value));
  return [];
}

export const AI_MODEL_ACTIVE_CAPABILITIES = ["tools", "reasoning", "websearch"] as const;

export const AI_MODEL_CAPABILITY_OPTIONS = toOptions(
  AI_MODEL_ACTIVE_CAPABILITIES,
  AI_MODEL_CAPABILITY_LABELS,
);

export const AI_MODEL_BILLING_MODE_OPTIONS = toOptions(
  AI_MODEL_BILLING_MODES,
  AI_MODEL_BILLING_MODE_LABELS,
);

export function aiModelBillingModeLabel(value: string): string {
  return AI_MODEL_BILLING_MODE_LABELS[value] ?? value;
}

export function kindHasOutputPrice(kind: string): boolean {
  return !(AI_MODEL_KINDS_WITHOUT_OUTPUT_PRICE as readonly string[]).includes(
    kind,
  );
}

export function aiModelKindLabel(kind: string): string {
  return AI_MODEL_KIND_LABELS[kind] ?? kind;
}

export function aiModelKindDescription(kind: string): string {
  return AI_MODEL_KIND_DESCRIPTIONS[kind] ?? "";
}

export function aiModelInputModalityLabel(value: string): string {
  return AI_MODEL_INPUT_MODALITY_LABELS[value] ?? value;
}

export function aiModelCapabilityLabel(value: string): string {
  return AI_MODEL_CAPABILITY_LABELS[value] ?? value;
}

export function kindHasModalityFields(kind: string): boolean {
  return (AI_MODEL_KINDS_WITH_MODALITIES as readonly string[]).includes(kind);
}

export function kindHasCapabilityFields(kind: string): boolean {
  return (AI_MODEL_KINDS_WITH_CAPABILITIES as readonly string[]).includes(
    kind,
  );
}

export function kindHasContextFields(kind: string): boolean {
  return (AI_MODEL_KINDS_WITH_CONTEXT as readonly string[]).includes(kind);
}

export type AiModelKindDraft = {
  kind: string;
  inputModalities: string[];
  inputLimits?: AiInputLimits;
  capabilities: string[];
  embedDim: number | null;
  contextWindow: number;
  maxOutputTokens: number;
  maxContextMessages: number;
  pricing: AiPricing;
};

export function applyKindChange<T extends AiModelKindDraft>(
  draft: T,
  nextKind: string,
): T {
  const next = {
    ...draft,
    kind: nextKind,
    pricing: newModelPricingForKind(nextKind),
    inputLimits: normalizeInputLimits(draft.inputLimits, normalizeModelInputModalities(nextKind, draft.inputModalities)),
  };

  if (nextKind === "embedding") {
    return {
      ...next,
      inputModalities: [],
      capabilities: [],
      contextWindow: 0,
      maxOutputTokens: 0,
      maxContextMessages: 0,
    };
  }

  if (nextKind === "rerank") {
    return {
      ...next,
      inputModalities: [],
      capabilities: [],
      embedDim: null,
      contextWindow: 0,
      maxOutputTokens: 0,
      maxContextMessages: 0,
    };
  }

  next.embedDim = null;

  next.inputModalities = normalizeModelInputModalities(nextKind, draft.inputModalities);

  if (!kindHasCapabilityFields(nextKind)) {
    next.capabilities = [];
  }

  if (!kindHasContextFields(nextKind)) {
    next.contextWindow = 0;
    next.maxOutputTokens = 0;
    next.maxContextMessages = 0;
  }

  return next;
}

export const AI_MODEL_SETTING_BOUNDS = {
  defaultTokenMultiplier: { default: 1, min: TOKEN_MULTIPLIER_MIN, max: TOKEN_MULTIPLIER_MAX },

  maxModelsPerProvider: { default: 20, min: 1, max: 1_000 },

  maxModelsPerUser: { default: 0, min: 0, max: 5_000 },

  maxModelsPerGroup: { default: 0, min: 0, max: 5_000 },

  upstreamTimeoutMs: { default: 15_000, min: 1_000, max: 120_000 },

  maxCatalogModels: { default: 5_000, min: 100, max: 50_000 },

  modelCacheTtlMs: { default: 60_000, min: 0, max: 3_600_000 },

  modelCacheMaxEntries: { default: 2_000, min: 100, max: 100_000 },

  fallbackContextWindow: { default: 8_192, min: 1_024, max: 2_000_000 },

  fallbackOutputReserve: { default: 2_048, min: 256, max: 200_000 },

  contextSafetyPercent: { default: 85, min: 50, max: 100 },

  agentInstructionsMax: { default: 8_000, min: 500, max: 50_000 },

  fieldMaxContextWindow: { default: 2_000_000, min: 8_192, max: 100_000_000 },

  fieldMaxOutputTokens: { default: 200_000, min: 1_024, max: 10_000_000 },

  fieldMaxContextMessages: { default: 200, min: 1, max: 10_000 },

  fieldModelIdMaxLength: { default: 128, min: 16, max: 512 },
} as const satisfies Record<string, { default: number; min: number; max: number }>;

export type AiModelSettingKey = keyof typeof AI_MODEL_SETTING_BOUNDS;

export type AiModelPlatformSettings = Record<AiModelSettingKey, number>;

export const AI_MODEL_SETTING_KEYS = Object.keys(
  AI_MODEL_SETTING_BOUNDS,
) as AiModelSettingKey[];

export const AI_MODEL_SETTINGS_DEFAULTS: AiModelPlatformSettings =
  Object.fromEntries(
    AI_MODEL_SETTING_KEYS.map((key) => [
      key,
      AI_MODEL_SETTING_BOUNDS[key].default,
    ]),
  ) as AiModelPlatformSettings;

export function normalizeAiModelSettings(
  raw: Record<string, unknown> | null | undefined,
): AiModelPlatformSettings {
  const result = {} as AiModelPlatformSettings;

  for (const key of AI_MODEL_SETTING_KEYS) {
    const bounds = AI_MODEL_SETTING_BOUNDS[key];
    const value = key === "defaultTokenMultiplier"
      ? (typeof raw?.[key] === "number" ? raw[key] : NaN)
      : Number(raw?.[key]);

    result[key] = Number.isFinite(value)
      ? Math.min(bounds.max, Math.max(bounds.min, key === "defaultTokenMultiplier" ? value : Math.round(value)))
      : bounds.default;
  }

  return result;
}

export function formatContextWindow(tokens: number): string {
  if (tokens <= 0) return "未设置";
  if (tokens >= 1_000_000) return `${Math.round(tokens / 100_000) / 10}M`;
  if (tokens >= 1000) return `${Math.round(tokens / 1024)}K`;

  return "tokens";
}
